import { useTheme } from '@mui/material';
import {
  ButtonPrimary,
  CustomTableSelect,
  Heading3,
  TextM,
} from 'app/components';
import { ArrowIcon, CopyIcon, HyperlinkIcon } from 'app/components/Icons';
import { formatDateFromBackend } from 'app/helpers/dateHelpers';
import { formatCurrency } from 'app/utils/normalizer';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ApplicationDetailsHeader,
  ViewBoxContainer,
  ViewBoxContent,
  ViewBoxItem,
} from '../../styles';
import { EAccountPermissions } from 'modules/auth/context/permify/permify.interfaces';
import { useAuthContext, usePermify } from 'modules/auth/context';
import { EQualificationStatus } from 'modules/products/context/QualificationsProvider/qualifications.interfaces';
import { useSnackbar } from 'app/hooks/useSnackbar';
import { handleStatusListPermissions } from 'modules/products/helpers/statusList';
import { TApplicationData } from '../../../ApplicationContent/ApplicationContent';
import { useOriginatorContext } from 'modules/common/OriginatorProvider';
import {
  EApplicationStatus,
  EApplicationStatusDescription,
} from 'modules/products/services/hooks/useApplicationsService';

const buttonStyle = {
  padding: '11px 15px',
  width: 'fit-content',
};

export interface IConditionContent {
  applicationData: TApplicationData | null;
  handleUpdateQualificationStatus: (
    application: TApplicationData | null,
    identifier: string,
  ) => void;
}

const ConditionContent = ({
  applicationData,
  handleUpdateQualificationStatus,
}: IConditionContent) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isProfileAuthorized } = usePermify();
  const { showSnackbarCopyLink } = useSnackbar();
  const { userInfo } = useAuthContext();
  const { setSelectedOriginator } = useOriginatorContext();

  const handleNavigate = () => {
    setSelectedOriginator(applicationData?.originator?.id as string);
    setTimeout(() =>
      navigate(`/products/application/${applicationData?.id}/details`, {
        state: { fromOperations: true },
      }),
    );
  };

  const handleClipboard = useCallback(() => {
    navigator.clipboard.writeText(
      `${document.location.origin}/#/products/application/${applicationData?.id}/details`,
    );
    showSnackbarCopyLink();
  }, []);

  const { showSnackbarCopy } = useSnackbar();

  const handleCopyId = (id?: string) => {
    if (!id) return;

    navigator.clipboard.writeText(String(id));
    showSnackbarCopy();
  };

  const statusList = useMemo(() => {
    if (!applicationData) return [];

    const { statusApproval, qualificationData, ...application } =
      applicationData;
    if (
      [
        EApplicationStatus.PENDING_QUALIFICATION,
        EApplicationStatus.PENDING_CESSION_QUALIFICATION,
      ].includes(applicationData.status as EApplicationStatus)
    ) {
      return [
        {
          label: 'Aprovado',
          value: EQualificationStatus.APPROVED as string,
        },
      ]
        .concat(
          statusApproval && statusApproval?.length > 0
            ? statusApproval.map((status) => ({
                label: status.description,
                value: status.identifier,
              }))
            : [],
        )
        .concat({
          label: 'Negado',
          value: EQualificationStatus.DENIED as string,
        });
    } else if (applicationData.status === EApplicationStatus.ISSUED) {
      return [
        {
          label: EApplicationStatusDescription[EApplicationStatus.ISSUED],
          value: EApplicationStatus.ISSUED,
        },
      ];
    } else if (
      applicationData.status === EApplicationStatus.PENDING_DISBURSEMENT
    ) {
      return [
        {
          label:
            EApplicationStatusDescription[
              EApplicationStatus.PENDING_DISBURSEMENT
            ],
          value: EApplicationStatus.PENDING_DISBURSEMENT,
        },
      ];
    } else if (
      applicationData.status === EApplicationStatus.PARTIAL_DISBURSEMENT
    ) {
      return [
        {
          label:
            EApplicationStatusDescription[
              EApplicationStatus.PARTIAL_DISBURSEMENT
            ],
          value: EApplicationStatus.PARTIAL_DISBURSEMENT,
        },
      ];
    } else if (applicationData.status === EApplicationStatus.PENDING_CESSION) {
      return [
        {
          label:
            EApplicationStatusDescription[EApplicationStatus.PENDING_CESSION],
          value: EApplicationStatus.PENDING_CESSION,
        },
        {
          label: EApplicationStatusDescription[EApplicationStatus.CANCELED],
          value: EApplicationStatus.CANCELED,
        },
      ];
    } else if (
      applicationData.status === EApplicationStatus.DISBURSEMENT_ATTEMPT_FAILED
    ) {
      return [
        {
          label:
            EApplicationStatusDescription[
              EApplicationStatus.DISBURSEMENT_ATTEMPT_FAILED
            ],
          value: EApplicationStatus.DISBURSEMENT_ATTEMPT_FAILED,
        },
      ];
    } else if (applicationData.status === EApplicationStatus.SIGNATURE_ERROR) {
      return [
        {
          label:
            EApplicationStatusDescription[EApplicationStatus.SIGNATURE_ERROR],
          value: EApplicationStatus.SIGNATURE_ERROR,
        },
        {
          label: EApplicationStatusDescription[EApplicationStatus.CANCELED],
          value: EApplicationStatus.CANCELED,
        },
      ];
    } else if (
      applicationData.status === EApplicationStatus.CREATING_ENTRY_PAYMENT
    ) {
      return [
        {
          label:
            EApplicationStatusDescription[
              EApplicationStatus.CREATING_ENTRY_PAYMENT
            ],
          value: EApplicationStatus.CREATING_ENTRY_PAYMENT,
        },
      ];
    } else if (
      applicationData.status === EApplicationStatus.PENDING_ENTRY_PAYMENT
    ) {
      return [
        {
          label:
            EApplicationStatusDescription[
              EApplicationStatus.PENDING_ENTRY_PAYMENT
            ],
          value: EApplicationStatus.PENDING_ENTRY_PAYMENT,
        },
      ];
    } else if (
      applicationData.status === EApplicationStatus.SENDING_CONSIGNEE_CONTRACT
    ) {
      return [
        {
          label:
            EApplicationStatusDescription[
              EApplicationStatus.SENDING_CONSIGNEE_CONTRACT
            ],
          value: EApplicationStatus.SENDING_CONSIGNEE_CONTRACT,
        },
      ];
    } else if (
      applicationData.status === EApplicationStatus.PENDING_CONTRACT_SUBMISSION
    ) {
      return [
        {
          label:
            EApplicationStatusDescription[
              EApplicationStatus.PENDING_CONTRACT_SUBMISSION
            ],
          value: EApplicationStatus.PENDING_CONTRACT_SUBMISSION,
        },
      ];
    } else if (
      applicationData.status === EApplicationStatus.SCHEDULED_DISBURSEMENT
    ) {
      return [
        {
          label:
            EApplicationStatusDescription[
              EApplicationStatus.SCHEDULED_DISBURSEMENT
            ],
          value: EApplicationStatus.SCHEDULED_DISBURSEMENT,
        },
        {
          label: EApplicationStatusDescription[EApplicationStatus.CANCELED],
          value: EApplicationStatus.CANCELED,
        },
      ];
    } else if (
      applicationData.status === EApplicationStatus.GUARANTEE_CANCEL_PROCESSING
    ) {
      return [
        {
          label:
            EApplicationStatusDescription[
              EApplicationStatus.GUARANTEE_CANCEL_PROCESSING
            ],
          value: EApplicationStatus.GUARANTEE_CANCEL_PROCESSING,
        },
      ];
    }
    return [
      {
        label:
          EApplicationStatusDescription[EApplicationStatus.AGREEMENT_RENDERING],
        value: EApplicationStatus.AGREEMENT_RENDERING,
      },
      {
        label:
          EApplicationStatusDescription[
            EApplicationStatus.CREATING_BENEFICIARY_ACCOUNT
          ],
        value: EApplicationStatus.CREATING_BENEFICIARY_ACCOUNT,
      },
      {
        label:
          EApplicationStatusDescription[
            EApplicationStatus.PARTIAL_DISBURSEMENT
          ],
        value: EApplicationStatus.PARTIAL_DISBURSEMENT,
      },
      {
        label:
          EApplicationStatusDescription[EApplicationStatus.PENDING_SIGNATURE],
        value: EApplicationStatus.PENDING_SIGNATURE,
      },
      {
        label:
          EApplicationStatusDescription[EApplicationStatus.PENDING_GUARANTEE],
        value: EApplicationStatus.PENDING_GUARANTEE,
      },
      {
        label:
          EApplicationStatusDescription[
            EApplicationStatus.PENDING_QUALIFICATION
          ],
        value: EApplicationStatus.PENDING_QUALIFICATION,
      },
      {
        label:
          EApplicationStatusDescription[EApplicationStatus.PENDING_PAYMENT],
        value: EApplicationStatus.PENDING_PAYMENT,
      },
      {
        label: EApplicationStatusDescription[EApplicationStatus.KYC_PROCESSING],
        value: EApplicationStatus.KYC_PROCESSING,
      },
      {
        label: EApplicationStatusDescription[EApplicationStatus.ISSUED],
        value: EApplicationStatus.ISSUED,
      },
      {
        label: EApplicationStatusDescription[EApplicationStatus.CANCELED],
        value: EApplicationStatus.CANCELED,
      },
    ]
      .filter((status) =>
        [applicationData.status, EApplicationStatus.CANCELED].includes(
          status.value,
        ),
      )
      .filter(
        handleStatusListPermissions(
          application,
          [
            EApplicationStatus.PENDING_QUALIFICATION,
            EApplicationStatus.PENDING_CESSION_QUALIFICATION,
          ].includes(application?.status as EApplicationStatus)
            ? qualificationData?.status
            : application?.status,
          userInfo,
        ),
      );
  }, [applicationData]);

  return (
    <>
      <ApplicationDetailsHeader>
        <Heading3>Condições</Heading3>

        <div style={{ display: 'flex', gap: '6px' }}>
          <ButtonPrimary
            typeVariant="outline"
            style={buttonStyle}
            onClick={handleClipboard}
          >
            <HyperlinkIcon color={theme.palette.brand.primary.base as string} />
          </ButtonPrimary>
          <ButtonPrimary
            typeVariant="outline"
            style={buttonStyle}
            onClick={handleNavigate}
          >
            <TextM
              weight="Semibold"
              style={{
                color: theme.palette.brand.primary.base,
                marginRight: '8px',
              }}
            >
              Ver detalhes
            </TextM>
            <ArrowIcon color={theme.palette.brand.primary.base as string} />
          </ButtonPrimary>
        </div>
      </ApplicationDetailsHeader>
      <ViewBoxContainer>
        <ViewBoxContent>
          <ViewBoxItem>
            <span>
              <TextM weight="Semibold">Status</TextM>
            </span>
            <span>
              <CustomTableSelect
                selectItems={statusList}
                isLoading={false}
                disabled={
                  !isProfileAuthorized(
                    EAccountPermissions.UPDATE_APPLICATION_QUALIFICATION,
                  ) || !applicationData?.productData
                }
                handleChange={(identifier) =>
                  handleUpdateQualificationStatus(applicationData, identifier)
                }
                value={
                  ([
                    EApplicationStatus.PENDING_QUALIFICATION,
                    EApplicationStatus.PENDING_CESSION_QUALIFICATION,
                  ].includes(applicationData?.status as EApplicationStatus)
                    ? applicationData?.qualificationData?.status
                    : applicationData?.status) || ''
                }
              />
            </span>
          </ViewBoxItem>
        </ViewBoxContent>
        <ViewBoxContent>
          <ViewBoxItem>
            <TextM weight="Semibold">ID</TextM>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <TextM>{applicationData?.id}</TextM>
              <ButtonPrimary
                typeVariant="ghost"
                style={{
                  width: 'fit-content',
                  margin: '0',
                  padding: '0 0 0 10px',
                }}
                onClick={() => handleCopyId(applicationData?.id)}
              >
                <CopyIcon color={theme.palette.brand.primary.base as string} />
              </ButtonPrimary>
            </div>
          </ViewBoxItem>
        </ViewBoxContent>
        <ViewBoxContent>
          <ViewBoxItem>
            <span>
              <TextM weight="Semibold">Valor solicitado</TextM>
            </span>
            <span>
              <TextM>{formatCurrency(applicationData?.requested_amount)}</TextM>
            </span>
          </ViewBoxItem>
        </ViewBoxContent>
        <ViewBoxContent>
          <ViewBoxItem>
            <span>
              <TextM weight="Semibold">Valor da parcela</TextM>
            </span>
            <span>
              <TextM>
                {formatCurrency(applicationData?.loan_details?.payment_amount)}
              </TextM>
            </span>
          </ViewBoxItem>
        </ViewBoxContent>
        <ViewBoxContent>
          <ViewBoxItem>
            <span>
              <TextM weight="Semibold">Valor Financiado</TextM>
            </span>
            <span>
              <TextM>
                {formatCurrency(applicationData?.loan_details?.financed_amount)}
              </TextM>
            </span>
          </ViewBoxItem>
        </ViewBoxContent>
        <ViewBoxContent>
          <ViewBoxItem>
            <span>
              <TextM weight="Semibold">Prazo</TextM>
            </span>
            <span>
              <TextM>
                {applicationData?.num_payments}{' '}
                {applicationData?.num_payments === 1 ? 'mês' : 'meses'}
              </TextM>
            </span>
          </ViewBoxItem>
        </ViewBoxContent>
        <ViewBoxContent>
          <ViewBoxItem>
            <span>
              <TextM weight="Semibold">Criado em</TextM>
            </span>
            <span>
              <TextM>
                {formatDateFromBackend(
                  applicationData?.created_at || '',
                ).toLocaleDateString()}
              </TextM>
            </span>
          </ViewBoxItem>
        </ViewBoxContent>
        <ViewBoxContent>
          <ViewBoxItem>
            <span>
              <TextM weight="Semibold">Vencimento da primeira parcela</TextM>
            </span>
            <span>
              <TextM>
                {formatDateFromBackend(
                  applicationData?.first_payment_date || '',
                ).toLocaleDateString()}
              </TextM>
            </span>
          </ViewBoxItem>
        </ViewBoxContent>
      </ViewBoxContainer>
    </>
  );
};

export default ConditionContent;
